import { getStack } from '@nuskin/contentstack-lib';

const cache = {};
const DEFAULT_ADDRESS_FORM = {
  addressElements: [],
  lookupField: 'address1',
  requiredFields: [],
  postalCodeRegex: null,
  verifyAfterAutocomplete: false,
  wrapAddress1ToAddress2: false
};

const coerceAddressElement = (element = {}) => {
  const nextElement = { ...element };

  if (nextElement.max_length !== undefined && nextElement.max_length !== null) {
    nextElement.max_length = Number(nextElement.max_length);
  }

  return nextElement;
};

const extractAddressForm = (addressForm = null) => {
  if (!addressForm || typeof addressForm !== 'object') {
    return addressForm;
  }

  return {
    ...addressForm,
    address_elements: Array.isArray(addressForm.address_elements) ?
      addressForm.address_elements.map(coerceAddressElement) :
      []
  };
};

const normalizeApiMappings = (apiMappings = null) => {
  if (!apiMappings || typeof apiMappings !== 'object') {
    return null;
  }

  const autocomplete = typeof apiMappings.autocomplete === 'string' ? apiMappings.autocomplete : '';
  const verification = typeof apiMappings.verification === 'string' ? apiMappings.verification : '';

  if (!autocomplete && !verification) {
    return null;
  }

  return {
    autocomplete: autocomplete || null,
    verification: verification || null
  };
};

const normalizeAllowedCharacters = (allowedCharacters) => {
  if (!allowedCharacters || typeof allowedCharacters !== 'string') {
    return null;
  }

  try {
    return new RegExp(allowedCharacters);
  } catch (err) {
    return null;
  }
};

const normalizeMaxLength = (element = {}) => {
  if (typeof element.maxLength === 'number') {
    return element.maxLength;
  }

  if (typeof element.max_length === 'number') {
    return element.max_length;
  }

  return null;
};

const normalizeWidth = (width) => {
  if (width === 'half' || width === 'third') {
    return width;
  }

  return 'full';
};

const normalizeAddressElement = (element = {}) => ({
  field: element.field || '',
  width: normalizeWidth(element.width),
  editable: Boolean(element.editable),
  maxLength: normalizeMaxLength(element),
  required: Boolean(element.required),
  allowedCharacters: normalizeAllowedCharacters(element.allowedCharacters || element.allowed_characters),
  apiMappings: normalizeApiMappings(element.api_mappings || element.apiMappings)
});

const normalizeRequiredFields = (addressElements = []) => {
  const seen = new Set();

  return addressElements
    .filter((element) => element.required && element.field)
    .map((element) => element.field)
    .filter((field) => {
      if (seen.has(field)) {
        return false;
      }
      seen.add(field);
      return true;
    });
};

const normalizePostalCodeRegex = (postalCodeRegex) => {
  if (!postalCodeRegex) {
    return null;
  }

  if (postalCodeRegex instanceof RegExp) {
    return postalCodeRegex;
  }

  if (typeof postalCodeRegex !== 'string') {
    return null;
  }

  try {
    return new RegExp(postalCodeRegex);
  } catch (err) {
    return null;
  }
};

export const normalizeAddressForm = (result = {}) => {
  const addressElementsSource = result.address_elements || result.addressElements;

  const addressElements = Array.isArray(addressElementsSource) ?
    addressElementsSource.map(normalizeAddressElement).filter((element) => element.field) :
    [];

  return {
    addressElements,
    lookupField: addressElements[0]?.field || DEFAULT_ADDRESS_FORM.lookupField,
    requiredFields: normalizeRequiredFields(addressElements),
    postalCodeRegex: normalizePostalCodeRegex(result.postal_code_regex || result.postalCodeRegex),
    verifyAfterAutocomplete: Boolean(result.verify_after_autocomplete || result.verifyAfterAutocomplete),
    wrapAddress1ToAddress2: Boolean(result.wrap_address1_to_address2 || result.wrapAddress1ToAddress2)
  };
};

export const getAddressForm = async (country) => {
  const countryCode = country || 'US';
  const cacheKey = `${countryCode}`;

  if (cache[cacheKey]) {
    return cache[cacheKey];
  }

  try {
    const stack = getStack();
    const results = await stack.getMultiEntries('address_form', true, [countryCode]);
    const form = normalizeAddressForm(extractAddressForm(results?.[0]));
    cache[cacheKey] = form;
    return form;
  } catch (err) {
    const fallback = { ...DEFAULT_ADDRESS_FORM };
    cache[cacheKey] = fallback;
    return fallback;
  }
};

export const clearAddressFormCache = () => {
  Object.keys(cache).forEach((cacheKey) => {
    delete cache[cacheKey];
  });
};

export { DEFAULT_ADDRESS_FORM };
